/**
 * Выгрузка карточек на маркетплейсы — вызов серверного экшена.
 *
 * Экшен `publishProductCards` сервиса wildberries-service. Операция идёт в
 * фоне: сервер отвечает { jobId }. jobId создаёт бэкенд (CLAUDE.md п.6) —
 * клиент его не выдумывает и без него считает запуск несостоявшимся.
 */

import { api } from '../../shared/api/client'
import { publishRequestBody } from './publishScope'
import type {
  PublishCardsRequest,
  PublishMarketplace,
  PublishMergeStatus,
  PublishPlan,
} from './types'

/** Единственное место, где зашит маршрут гейтвея к publishProductCards */
export const PUBLISH_CARDS_ENDPOINT = '/cards/publish'
export const PUBLISH_PLAN_ENDPOINT = '/cards/publish/plan'
export const MERGE_RETRY_ENDPOINT = '/cards/merge/retry'

export interface MarketplaceMergeRetryResult {
  processed: number
  merged: number
  failed: number
  skipped: number
  results: Array<{
    card_id: number
    merge_label: string | null
    imt_id: number | null
    merge_status: PublishMergeStatus
  }>
}

/** Состояние задачи по запросу: тот же ресурс, что читает виджет фоновых задач */
export const publishJobEndpoint = (jobId: string): string =>
  `/jobs/${encodeURIComponent(jobId)}`

const unwrap = (response: any) => response?.data?.data ?? response?.data

export function publishRequestError(error: any, fallback: string): string {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  )
}

export const publishCardsAPI = {
  async retryMarketplaceCardMerge(cardIds: number[]): Promise<MarketplaceMergeRetryResult> {
    const exactIds = [...new Set(cardIds
      .map(Number)
      .filter((cardId) => Number.isSafeInteger(cardId) && cardId > 0))]
    if (exactIds.length === 0) throw new Error('Не выбраны карточки для повторной склейки')
    const response = await api.post(MERGE_RETRY_ENDPOINT, { card_ids: exactIds })
    const data = unwrap(response) || {}
    if (data.success === false) {
      throw new Error(data?.error?.message || 'Повторная склейка отклонена')
    }
    const mergeStatuses: PublishMergeStatus[] = [
      'merged', 'skipped_disabled', 'failed', 'not_applicable',
    ]
    const results = (Array.isArray(data.results) ? data.results : []).flatMap((row: any) => {
      const cardId = Number(row?.card_id)
      const imtId = Number(row?.imt_id)
      const mergeStatus = String(row?.merge_status || '') as PublishMergeStatus
      if (!Number.isSafeInteger(cardId) || cardId <= 0 || !mergeStatuses.includes(mergeStatus)) {
        return []
      }
      return [{
        card_id: cardId,
        merge_label: String(row?.merge_label || '').trim() || null,
        imt_id: Number.isSafeInteger(imtId) && imtId > 0 ? imtId : null,
        merge_status: mergeStatus,
      }]
    })
    return {
      processed: Math.max(0, Math.trunc(Number(data.processed) || 0)),
      merged: Math.max(0, Math.trunc(Number(data.merged) || 0)),
      failed: Math.max(0, Math.trunc(Number(data.failed) || 0)),
      skipped: Math.max(0, Math.trunc(Number(data.skipped) || 0)),
      results,
    }
  },

  /** Создаёт временный набор точных карточек для страницы предпросмотра. */
  async createPublishPlan(
    cardIds: number[],
    marketplace?: PublishMarketplace | null,
  ): Promise<string> {
    const response = await api.post(PUBLISH_PLAN_ENDPOINT, {
      card_ids: cardIds,
      marketplace,
      save: true,
    })
    const data = unwrap(response) || {}
    if (data.success === false) {
      throw new Error(data?.error?.message || 'Маркетплейс отклонил сборку набора')
    }
    const planId = String(data.plan_id ?? '').trim()
    if (!planId) {
      throw new Error('Сервер не вернул plan_id — набор не создан')
    }
    return planId
  },

  /** Возвращает jobId сервера; бросает, если сервер его не дал. */
  async publishProductCards(plan: PublishPlan): Promise<string> {
    const body: PublishCardsRequest = publishRequestBody(plan)
    const response = await api.post(PUBLISH_CARDS_ENDPOINT, body)
    const data = unwrap(response) || {}
    if (data.success === false) {
      throw new Error(data?.error?.message || 'Маркетплейс отклонил запуск выгрузки')
    }
    const jobId = String(data.jobId ?? data.job_id ?? '').trim()
    if (!jobId) {
      throw new Error('Сервер не вернул jobId — выгрузка не запущена')
    }
    return jobId
  },

  /**
   * Состояние задачи выгрузки одним запросом.
   *
   * Окно не имеет права полагаться только на события: задача может завершиться
   * до того, как окно на них подпишется, а завершённая задача исчезает из
   * хранилища фоновых задач через пять секунд. Запрос отвечает на вопрос
   * «что с задачей сейчас» независимо от того, что окно успело услышать.
   *
   * `null` — сервер такой задачи не знает; это не ошибка, а ответ.
   */
  async fetchPublishJob(jobId: string): Promise<Record<string, any> | null> {
    const response = await api.get(publishJobEndpoint(jobId))
    const data = unwrap(response)
    if (!data || typeof data !== 'object') return null
    const job = data as Record<string, any>
    // `unwrap` не отличает `{ success: true, data: null }` от самой задачи:
    // признак настоящей задачи — её идентификатор, по нему и судим.
    if (!job.jobId && !job.job_id) return null
    return job
  },
}
